/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from "@/assets/images/logo.jpg";
import saladRollsHero from "@/assets/images/salad-rolls-hero.jpg";
import crispyEggRolls from "@/assets/images/crispy-egg-rolls.jpg";
import specialRiceCombo from "@/assets/images/special-rice-combo.jpg";
import porkAndShrimp from "@/assets/images/pork-and-shrimp.jpg";
import vegetarianSaladRolls from "@/assets/images/vegetarian-salad-rolls.jpg";
import pageBackground from "@/assets/images/page-background.jpg";
import d1SpecialRiceCombo from "@/assets/images/d1-special-rice-combo.jpg";
import d2PorkChopEggs from "@/assets/images/d2-pork-chop-eggs.jpg";
import d3PorkShrimp from "@/assets/images/d3-pork-shrimp.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo
  "/wp-content/uploads/2017/05/dangs-vietnamese-restaurant-bend-oregon_phone-300x125.jpg": logo,
  "/images/logo.jpg": logo,
  "/assets/images/logo.jpg": logo,
  
  // Hero slider images
  "/wp-content/uploads/2014/03/salad-rolls-a1-dangs-vietnamese-bend-oregon-1400x465.jpg": saladRollsHero,
  "/images/salad-rolls-hero.jpg": saladRollsHero,
  "/assets/images/salad-rolls-hero.jpg": saladRollsHero,
  
  "/wp-content/uploads/2014/03/imperial-rolls-a3-dangs-vietnamese-bend-oregon-212x213.jpg": crispyEggRolls,
  "/images/crispy-egg-rolls.jpg": crispyEggRolls,
  "/assets/images/crispy-egg-rolls.jpg": crispyEggRolls,
  
  "/wp-content/uploads/2014/03/special-rice-combo-d1-dangs-vietnamese-bend-oregon-212x213.jpg": specialRiceCombo,
  "/images/special-rice-combo.jpg": specialRiceCombo,
  "/assets/images/special-rice-combo.jpg": specialRiceCombo,
  
  "/wp-content/uploads/2014/03/pork-and-shrimp-d3-dangs-vietnamese-bend-oregon-212x213.jpg": porkAndShrimp,
  "/images/pork-and-shrimp.jpg": porkAndShrimp,
  "/assets/images/pork-and-shrimp.jpg": porkAndShrimp,
  
  "/wp-content/uploads/2014/03/special-combo-v1-dangs-vietnamese-bend-oregon-212x213.jpg": vegetarianSaladRolls,
  "/images/vegetarian-salad-rolls.jpg": vegetarianSaladRolls,
  "/assets/images/vegetarian-salad-rolls.jpg": vegetarianSaladRolls,
  
  "/wp-content/themes/MyCuisine/images/page-top.jpg": pageBackground,
  "/images/page-background.jpg": pageBackground,
  "/assets/images/page-background.jpg": pageBackground,
  
  // Chef recommendations images
  "/wp-content/uploads/2014/03/special-rice-combo-d1-dangs-vietnamese-bend-oregon-56x56.jpg": d1SpecialRiceCombo,
  "/images/d1-special-rice-combo.jpg": d1SpecialRiceCombo,
  "/assets/images/d1-special-rice-combo.jpg": d1SpecialRiceCombo,
  
  "/wp-content/uploads/2014/03/pork-chop-and-egg-d2-dangs-vietnamese-bend-oregon-56x56.jpg": d2PorkChopEggs,
  "/images/d2-pork-chop-eggs.jpg": d2PorkChopEggs,
  "/assets/images/d2-pork-chop-eggs.jpg": d2PorkChopEggs,
  
  "/wp-content/uploads/2014/03/pork-and-shrimp-d3-dangs-vietnamese-bend-oregon-56x56.jpg": d3PorkShrimp,
  "/images/d3-pork-shrimp.jpg": d3PorkShrimp,
  "/assets/images/d3-pork-shrimp.jpg": d3PorkShrimp,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
